import androidx.compose.runtime.*
import kotlinx.coroutines.CancellationException
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*

private fun TourPerformanceAward.isScoreAward() = label.trim() in listOf("300", "20")

@Composable
fun TourPublicResults(slug: String) {
    var bundle by remember(slug) { mutableStateOf<TourPlayBundle?>(null) }
    var tournament by remember(slug) { mutableStateOf<TourTournamentDto?>(null) }
    var error by remember(slug) { mutableStateOf<String?>(null) }
    var loading by remember(slug) { mutableStateOf(true) }
    var retry by remember { mutableStateOf(0) }
    var selectedTab by remember(slug) { mutableStateOf("results") }
    LaunchedEffect(slug, retry) {
        loading = true; error = null; bundle = null; tournament = null
        try {
            tournament = TourOperationsApi.tournamentBySlug(slug)
            tournament?.let { bundle = TourPlayApi.bundle(null, it.id) }
        } catch (e: Throwable) { if (e is CancellationException) throw e; error = e.apiMessage() }
        finally { loading = false }
    }
    Div(attrs = { classes("card") }) {
        A(href = tourBase() + "/") { Text("← Aktuálně") }
        H2 { Text(tournament?.title ?: "Výsledky Tour") }
        tournament?.let { t ->
            P { Text("${t.startsOn?.let(::formatDate) ?: "Termín bude upřesněn"} · ${tourLabel(t.discipline, TOUR_DISCIPLINES)}") }
            P { Text(tourLabel(t.status, TOUR_TOURNAMENT_STATES)) }
            if (t.description.isNotBlank()) P { Text(t.description) }
        }
        TourError(error)
        if (error != null) Button(attrs = { onClick { retry++ } }) { Text("Načíst znovu") }
        if (loading) P { Text("Načítám výsledky…") }
        if (!loading && error == null && bundle == null) P { Text("Turnaj nebyl nalezen nebo zatím nebyl zveřejněn.") }
        if (bundle?.correctionMode == true) P { Text("Výsledky se opravují. Zobrazuje se poslední zveřejněná verze.") }
        if (bundle != null) Div(attrs = { classes("tour-result-tabs"); attr("role", "group"); attr("aria-label", "Zobrazení výsledků turnaje") }) {
            listOf("results" to "Konečné pořadí", "qualification" to "Kvalifikace", "semifinal" to "Semifinále", "awards" to "Prémie").forEach { (tab, label) ->
                Button(attrs = {
                    type(ButtonType.Button); id("tour-result-tab-$tab")
                    if (selectedTab == tab) classes("primary")
                    attr("aria-pressed", (selectedTab == tab).toString()); attr("aria-controls", "tour-result-panel")
                    onClick { selectedTab = tab }
                }) { Text(label) }
            }
        }
    }
    val b = bundle ?: return
    val q = b.published?.qualification ?: b.qualification
    val s = b.published?.semifinal ?: b.semifinal
    val f = b.published?.final ?: b.final
    val awards = b.published?.awards ?: b.awards
    val results = b.published?.results ?: q.filter { it.played > 0 }.map {
        TourResultRow(it.entryId, it.playerName, it.handicap, false, qualificationRank = it.rank,
            withdrawnPhase = it.withdrawnPhase, dnf = !it.complete, pending = it.tiePending,
            baseRank = 0, participantCount = q.size)
    }
    val special = if (tournament?.discipline == "narubak") 20 else 300
    val semifinalists = s.map { it.entryId }.toSet()
    val finalists = f.map { it.entryId }.toSet()
    val finishPhases = results.map { r ->
        r.withdrawnPhase ?: when (r.entryId) {
            in finalists -> "final"
            in semifinalists -> "semifinal"
            else -> "qualification"
        }
    }
    if (selectedTab == "results") Div(attrs = { classes("card"); id("tour-result-panel"); attr("role", "region"); attr("aria-labelledby", "tour-result-tab-results") }) {
        H2 { Text(if (b.published != null) "Konečné pořadí" else "Průběžné výsledky") }
        if (b.published == null) P(attrs = { classes("muted") }) { Text("Průběžné náhozy; konečné pořadí dosud nebylo zveřejněno.") }
        if (results.isEmpty()) P { Text("Zatím nejsou odehrané výsledky.") }
        else Div(attrs = { classes("table-scroll") }) {
            Table(attrs = { classes("public-table", "tour-score-table", "tour-public-summary"); attr("aria-label", "Konečné pořadí Tour") }) {
                Thead {
                    Tr(attrs = { classes("tour-result-groups") }) {
                        Th(attrs = { colspan(7); attr("scope", "colgroup") }) { Text("Finále") }
                        Th(attrs = { colspan(6); attr("scope", "colgroup"); classes("tour-qualification-cell", "tour-group-start") }) { Text("Kvalifikace") }
                        Th(attrs = { colspan(2); attr("scope", "colgroup"); classes("tour-awards-cell", "tour-group-start") }) { Text("Prémie") }
                    }
                    Tr { listOf("Pořadí", "Hráč", "Součet finále", "SM hra1", "SM hra2", "Finále", "HDCP", "Pořadí kvalifikace", "Součet kvalifikace", "1. hra", "2. hra", "3. hra", "HDCP", "Prémie", special.toString()).forEachIndexed { index, label ->
                        Th(attrs = {
                            attr("scope", "col")
                            if (index in 7..12) classes("tour-qualification-cell")
                            if (index >= 13) classes("tour-awards-cell")
                            if (index in listOf(7,13)) classes("tour-group-start")
                        }) { Text(label) }
                    } }
                }
                Tbody { results.forEachIndexed { index, r ->
                    val qualification = q.firstOrNull { it.entryId == r.entryId }
                    val semifinal = s.firstOrNull { it.entryId == r.entryId }
                    val final = f.firstOrNull { it.entryId == r.entryId }
                    val finalBlock = final ?: semifinal
                    val premiums = awards.filter { it.entryId == r.entryId && !it.isScoreAward() }.sumOf { it.quantity }
                    val missingAwardPhases = awards.any { it.entryId == r.entryId && !it.isScoreAward() && it.phase == null }
                    val specialCount = listOfNotNull(qualification?.game1, qualification?.game2, qualification?.game3,
                        semifinal?.game1, semifinal?.game2, final?.game3).count { it == special }
                    Tr(attrs = {
                        attr("data-finish-phase", finishPhases[index])
                        if (index > 0 && finishPhases[index] != finishPhases[index - 1]) classes("tour-finish-group-start")
                    }) {
                        Td(attrs = { classes("tour-rank-cell") }) { r.rank?.let { RankBadge(it) } ?: Text("—") }
                        Td(attrs = { classes("tour-player-name") }) {
                            Text(r.playerName)
                            if (r.joker) Span(attrs = { classes("tour-result-flag") }) { Text("Joker") }
                            if (r.dnf || r.withdrawnPhase != null || r.pending) Span(attrs = { classes("tour-result-flag") }) {
                                Text(if (r.pending) "Nerozhodnutá shoda" else if (r.dnf) "Nedokončil kvalifikaci" else "Odstoupil")
                            }
                        }
                        Td { Span(attrs = { classes("pill") }) { Text(finalBlock?.adjustedTotal?.toString() ?: "—") } }
                        Td { Text(semifinal?.game1?.toString() ?: "—") }
                        Td { Text(semifinal?.game2?.toString() ?: "—") }
                        Td { Text(final?.game3?.toString() ?: "—") }
                        Td { Text(finalBlock?.let { (it.handicap * it.played).toString() } ?: "—") }
                        Td(attrs = { classes("tour-qualification-cell", "tour-group-start") }) {
                            r.qualificationRank?.let { RankBadge(it, showMedals = false) } ?: Text("—")
                        }
                        Td(attrs = { classes("tour-qualification-cell") }) { Text(qualification?.adjustedTotal?.toString() ?: "—") }
                        listOf(qualification?.game1, qualification?.game2, qualification?.game3).forEach { game ->
                            Td(attrs = { classes("tour-qualification-cell") }) { Text(game?.toString() ?: "—") }
                        }
                        Td(attrs = { classes("tour-qualification-cell") }) { Text(qualification?.let { (it.handicap * it.played).toString() } ?: "—") }
                        Td(attrs = { classes("tour-awards-cell", "tour-group-start") }) { Text(if (missingAwardPhases) "—" else premiums.toString()) }
                        Td(attrs = { classes("tour-awards-cell") }) { Text(specialCount.toString()) }
                    }
                } }
            }
        }
    }
    listOf("qualification" to q, "semifinal" to s).filter { it.first == selectedTab }.forEach { (phase, rows) ->
        val advancementConfirmed = b.published != null || if (phase == "qualification") b.phase != "qualification" else b.phase in listOf("final", "finished")
        Div(attrs = { classes("card"); id("tour-result-panel"); attr("role", "region"); attr("aria-labelledby", "tour-result-tab-$phase") }) {
            H2 { Text(tourLabel(phase, TOUR_PHASES)) }
            TourPublicPhaseTable(rows, phase, if (advancementConfirmed) {
                if (phase == "qualification") semifinalists else finalists
            } else null)
        }
    }
    if (selectedTab == "awards") Div(attrs = { classes("card"); id("tour-result-panel"); attr("role", "region"); attr("aria-labelledby", "tour-result-tab-awards") }) {
        H2 { Text("Prémie") }
        if (awards.any { !it.isScoreAward() && it.phase == null }) P { Text("Rozpis starších prémií do jednotlivých fází se doplňuje.") }
        val phasedAwards = awards.filter { !it.isScoreAward() && it.phase != null }
        if (awards.none { !it.isScoreAward() }) P { Text("Zatím nejsou evidované výkonové prémie.") }
        if (phasedAwards.isNotEmpty()) Div(attrs = { classes("table-scroll") }) {
            Table(attrs = { classes("public-table", "tour-score-table"); attr("aria-label", "Prémie podle fází") }) {
                Thead { Tr { listOf("Jméno", "Počet celkem", "Kvalifikace", "Semifinále", "Finále").forEach { Th { Text(it) } } } }
                Tbody {
                    phasedAwards.groupBy { it.entryId }.entries.sortedWith(compareBy(tourNameComparator) { entry ->
                        q.firstOrNull { it.entryId == entry.key }?.playerName.orEmpty()
                    }).forEach { (entry, playerAwards) -> Tr {
                        Td(attrs = { classes("tour-player-name") }) { Text(q.firstOrNull { it.entryId == entry }?.playerName ?: results.firstOrNull { it.entryId == entry }?.playerName ?: "Hráč") }
                        Td { Span(attrs = { classes("pill") }) { Text(playerAwards.sumOf { it.quantity }.toString()) } }
                        TOUR_PHASES.map { it.first }.forEach { phase ->
                            Td { Text(playerAwards.filter { it.phase == phase }.sumOf { it.quantity }.toString()) }
                        }
                    } }
                }
            }
        }
    }
}

@Composable
private fun TourPublicPhaseTable(rows: List<TourStageRow>, phase: String, advancingIds: Set<String>?) {
    if (rows.isEmpty()) { P { Text("Zatím nejsou odehrané výsledky.") }; return }
    val limit = if (phase == "qualification") 24 else 15
    val lastAdvancing = rows.indexOfLast { row ->
        advancingIds?.contains(row.entryId) ?: (row.complete && row.rank != null && row.rank <= limit && row.withdrawnPhase != phase && !row.tiePending)
    }
    Div(attrs = { classes("table-scroll") }) {
        Table(attrs = { classes("public-table", "tour-score-table"); attr("aria-label", "Náhozy ${tourLabel(phase, TOUR_PHASES)}") }) {
            Thead { Tr {
                listOf("Pořadí", "Hráč", "Součet", "1. hra", "2. hra").forEach { Th { Text(it) } }
                if (phase == "qualification") Th { Text("3. hra") }
                Th { Text("Součet bez HDCP") }; Th { Text("HDCP") }
            } }
            Tbody { rows.forEachIndexed { index, r -> Tr(attrs = {
                if (lastAdvancing >= 0 && index == lastAdvancing + 1) classes("tour-phase-cut")
            }) {
                Td(attrs = { classes("tour-rank-cell") }) { r.rank?.let { RankBadge(it) } ?: Text(if (r.tiePending) "Shoda" else "—") }
                Td(attrs = { classes("tour-player-name") }) {
                    Text(r.playerName)
                    if (r.withdrawnPhase == phase) Span(attrs = { classes("tour-result-flag") }) { Text("Odstoupil") }
                }
                Td { Span(attrs = { classes("pill") }) { Text(r.adjustedTotal?.toString() ?: "—") } }
                Td { Text(r.game1?.toString() ?: "—") }; Td { Text(r.game2?.toString() ?: "—") }
                if (phase == "qualification") Td { Text(r.game3?.toString() ?: "—") }
                Td { Text(r.rawTotal?.toString() ?: "—") }; Td { Text((r.handicap * r.played).toString()) }
            } } }
        }
    }
}
